import {printIntent} from '../../common/printIntent';
declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData?: string;
        colorScheme?: "light" | "dark";
        ready?: () => void;
        expand?: () => void;
        openLink?: (url: string, options?: {try_instant_view?: boolean}) => void;
        onEvent?: (event: "themeChanged", callback: () => void) => void;
        offEvent?: (event: "themeChanged", callback: () => void) => void;
        BackButton?: {
          show?: () => void;
          hide?: () => void;
          onClick?: (callback: () => void) => void;
          offClick?: (callback: () => void) => void;
        };
      };
      Login?: {
        auth?: (
          options: { client_id: string | number; scope?: string[] | string; nonce?: string; lang?: string },
          callback: (result: { id_token?: string; user?: unknown; error?: string }) => void,
        ) => void;
      };
    };
  }
}

export type AuthMePayload = {
  ok?: boolean;
  authenticated?: boolean;
  user?: Record<string, unknown>;
  access?: Record<string, unknown>;
  tenant?: Record<string, unknown>;
  login?: {
    telegram_login_enabled?: boolean;
    telegram_login_client_id?: string;
    telegram_login_scope?: string[];
  };
  detail?: string;
};

export type SettingsPayload = {
  app_version?: string;
  tenant_code?: string;
  client_name?: string;
  is_admin?: boolean;
  tenants?: Array<{
    tenant_code?: string;
    name?: string;
    role?: string;
    is_active?: boolean;
    is_current?: boolean;
  }>;
  admin_ui?: {
    default_path?: string;
    canonical_path?: string;
    fallback_path?: string;
    v2_path?: string;
  };
  sms_gateway_enabled?: boolean;
  sms_order_created_default_enabled?: boolean;
  sms_notification_trigger?: "disabled" | "order_created" | "shipment_warehouse";
  storage_expiry_sms_enabled?: boolean;
  show_legal_documents?: boolean;
  printnode_enabled?: boolean;
  printnode_default_enabled?: boolean;
  print_provider?: string;
  print_provider_label?: string;
  order_items_enabled?: boolean;
  adult_goods_enabled?: boolean;
  cod_auto_fill_enabled?: boolean;
  cod_auto_fill_default?: boolean;
  delivery_block_enabled?: boolean;
  delivery_block_default?: boolean;
  delivery_block_selectable?: boolean;
  cdek_profile?: Record<string, unknown>;
  feature_flags?: Record<string, unknown>;
};

const ACTIVE_TENANT_STORAGE_KEY = "cdek_active_tenant";
let activeTenantCode = window.localStorage.getItem(ACTIVE_TENANT_STORAGE_KEY) || "";

export function currentTenantCode(): string {
  return activeTenantCode;
}

export function setActiveTenantCode(value: string): void {
  activeTenantCode = String(value || "").trim().toLowerCase();
  if (activeTenantCode) window.localStorage.setItem(ACTIVE_TENANT_STORAGE_KEY, activeTenantCode);
  else window.localStorage.removeItem(ACTIVE_TENANT_STORAGE_KEY);
}

export type OrderSummary = {
  creation_retry?: CreationRetry;
  creation_state?: "created" | "pending" | "rejected";
  creation_error?: string;
  internal_order_number?: string;
  cdek_number?: string;
  cdek_uuid?: string;
  moysklad_order_number?: string;
  recipient_name?: string;
  phone?: string;
  recipient_phone?: string;
  city_name?: string;
  pvz_code?: string;
  pvz_address?: string;
  tariff_code?: number;
  tariff_name?: string;
  cod_fee?: number;
  cod_auto_fill?: boolean;
  delivery_block?: boolean;
  delivery_block_markup?: number;
  declared_value_manual?: boolean;
  declared_value_requested?: number;
  declared_value_per_package?: number;
  declared_value_effective?: number;
  adult_goods?: boolean | number;
  telegram_user_id?: number;
  created_by_telegram_user_id?: number;
  created_by_username?: string;
  created_by_display?: string;
  cdek_profile_name?: string;
  cdek_profile_status?: string;
  cdek_profile_known?: boolean;
  cdek_profile_problem?: string;
  cdek_profile_hint?: string;
  latest_status_name?: string;
  latest_status_code?: string;
  latest_status_city?: string;
  latest_status_at?: string;
  created_at?: string;
  tracking_url?: string;
  cdek_profile_code?: string;
  cdek_profile?: Record<string, unknown>;
  print_barcode_requested?: boolean;
  sms_order_created_requested?: boolean;
  sms_notification_trigger?: "disabled" | "order_created" | "shipment_warehouse";
  order_origin?: "import" | "service" | "webhook";
  document_delivery_error?: boolean;
  channel_delivery_error?: boolean;
  sent_documents?: string[];
  keep_free_until?: string;
  status_sync_error?: string;
  has_return?: boolean;
};

export type CreationRetry = {
  moysklad: string;
  packages: PackageInput[];
  codFee: number;
  codAutoFill: boolean;
  deliveryBlock: boolean;
  deliveryBlockAmount: number | null;
  printBarcode: boolean;
  sendSms: boolean;
  manualDeclaredValue: number | null;
};

export type OrdersPayload = {
  orders?: OrderSummary[];
  facets?: Record<string, number>;
  pagination?: {
    page?: number;
    limit?: number;
    total?: number;
    pages?: number;
  };
  cdek_profiles?: {
    active_profile?: Record<string, unknown>;
    profiles?: Record<string, unknown>[];
    diagnostics?: Record<string, unknown>;
  };
};

export type OrderDetailPayload = {
  order?: OrderSummary & {
    statuses?: Record<string, unknown>[];
    events?: Record<string, unknown>[];
    audit_events?: Record<string, unknown>[];
    packages?: Record<string, unknown>[];
    items?: Record<string, unknown>[];
    notification_events?: Record<string, unknown>[];
    operational_events?: Record<string, unknown>[];
    document_jobs?: Record<string, unknown>[];
    print_jobs?: Record<string, unknown>[];
    print_attempts?: Record<string, unknown>[];
    returns?: Record<string, unknown>[];
    linked_orders?: Record<string, unknown>[];
    is_return_waybill?: boolean;
    status_refresh_error?: string;
    printnode_available?: boolean;
    printnode_printer_id?: number;
    print_provider?: string;
    print_provider_label?: string;
    printer_key?: string;
    print_settings?: {
      provider?: string;
      provider_label?: string;
      enabled?: boolean;
      printer_key?: string;
      printer_id?: number;
      paper?: string;
      fit_to_page?: boolean;
      media_width_mm?: number;
      media_height_mm?: number;
      dpi?: number;
    };
    city_code?: number;
    country_code?: string;
    can_delete?: boolean;
    delete_requires_api?: boolean;
  };
};

export type OrderItemInput = {
  name: string;
  amount: number;
  cost: number;
};

export type PackageInput = {
  length: number;
  width: number;
  height: number;
  weight: number;
  items?: OrderItemInput[];
};

export type RecipientOption = {
  id?: number;
  recipient_name?: string;
  phone?: string;
  city_name?: string;
  city_code?: number;
  pvz_code?: string;
  pvz_address?: string;
  country_code?: string;
  adult_goods?: boolean | number;
  last_used_at?: string;
  updated_at?: string;
};

export type CityOption = {
  code?: number;
  city?: string;
  city_name?: string;
  region?: string;
  country_code?: string;
};

export type DeliveryPointOption = {
  code?: string;
  name?: string;
  address?: string;
  full_address?: string;
  type?: string;
  status?: string;
  allowed_cod?: boolean;
  location?: Record<string, unknown>;
  requested_code?: string;
  replacement_applied?: boolean;
  replacement_code?: string;
  replacement_reason?: string;
};

export type DeliveryPointLookupPayload = {
  city?: CityOption;
  point?: DeliveryPointOption;
  requested_code?: string;
  replacement_applied?: boolean;
  replacement_code?: string;
  replacement_reason?: string;
};

export type TariffOption = {
  tariff_code?: number;
  tariff_name?: string;
  delivery_sum?: number;
  period_min?: number;
  period_max?: number;
};

export type TariffDetail = {
  tariff_code?: number;
  total_sum?: number;
  delivery_sum?: number;
  insurance_sum?: number;
  adult_goods_sum?: number;
  delivery_block_sum?: number;
  cod_markup_sum?: number;
  cod_auto_fill_sum?: number;
  cod_recommendation_sum?: number;
  cod_recommendation_label?: string;
  recommended_cod_fee?: number;
  cod_markup_minimum_applied?: boolean;
  period_min?: number;
  period_max?: number;
  currency?: string;
};

export type OrderCreatePayload = {
  recipient_name: string;
  phone: string;
  city_name: string;
  city_code: number;
  recipient_pvz: string;
  recipient_pvz_address: string;
  moysklad_order_number: string;
  selected_recipient_id: number | null;
  selected_tariff: number;
  cod_fee: number;
  cod_auto_fill: boolean;
  delivery_block: boolean;
  delivery_block_amount?: number | null;
  print_barcode: boolean;
  send_sms: boolean;
  adult_goods: boolean;
  manual_declared_value: number | null;
  packages: PackageInput[];
};

export type OrderPreflightPayload = {
  ok?: boolean;
  errors?: string[];
  warnings?: string[];
  tariff_detail?: TariffDetail;
  calculated_cod_fee?: number;
  calculated_delivery_block_sum?: number;
  declared_value_manual?: boolean;
  declared_value_requested?: number;
  declared_value_per_package?: number;
  declared_value_effective?: number;
  declared_value_package_count?: number;
  declared_value_rounding_adjustment?: number;
  cod_available?: boolean;
  delivery_point?: Record<string, unknown>;
  cdek_profile?: Record<string, unknown>;
};

export type OrderCreateResult = {
  internal_order_number?: string;
  moysklad_order_number?: string;
  cdek_number?: string;
  entity_uuid?: string;
  uuid?: string;
  create_status?: number;
  local_order_save_error?: boolean;
  [key: string]: unknown;
};

export type BillingSummary = {
  tenant_code: string;
  billing_enabled: boolean;
  status: string;
  plan_name?: string;
  balance_rub: number;
  posted_balance_rub: number;
  accrued_sms_rub: number;
  accrued_sms_count?: number;
  credit_limit_rub: number;
  available_rub: number;
  low_balance_threshold_rub: number;
  read_only: boolean;
  sms_enabled: boolean;
  next_charge_on?: string;
  payment_provider_ready?: boolean;
};

export type BillingEntry = {
  id: number;
  entry_type: string;
  amount_rub: number;
  period_start?: string;
  period_end?: string;
  comment?: string;
  payment_provider?: string;
  created_at?: string;
};

export type BillingPayment = {
  id: number;
  provider?: string;
  amount_rub: number;
  status: string;
  provider_payment_id?: string;
  created_at?: string;
  updated_at?: string;
  confirmed_at?: string;
  attempts?: Array<{
    provider_transaction_id: string;
    event_kind: string;
    status: string;
    amount_rub: number;
    provider_event_at?: string;
    error_code?: string;
    error_message?: string;
    received_count: number;
    first_received_at?: string;
    last_received_at?: string;
  }>;
};

export type BillingSmsUsage = {
  id: number;
  provider?: string;
  provider_message_id?: string;
  order_internal_number?: string;
  target?: string;
  message_type?: string;
  message_text?: string;
  unit_price_rub: number;
  accepted_at?: string;
  billing_month?: string;
  ledger_entry_id?: number | null;
  delivery_status?: string;
  status_at?: string;
  error_detail?: string;
  cdek_number?: string;
  moysklad_order_number?: string;
};

export type PaymentAction = {
  type: "redirect";
  provider: string;
  url?: string;
};

export const telegramInitData = (): string => window.Telegram?.WebApp?.initData || "";

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers || {});
  const hasBody = typeof options.body !== "undefined";
  if (hasBody) headers.set("Content-Type", "application/json");
  const initData = telegramInitData();
  if (initData) headers.set("X-Telegram-Init-Data", initData);
  if (activeTenantCode) headers.set("X-CDEK-Tenant", activeTenantCode);

  const response = await fetch(path, {
    ...options,
    cache: "no-store",
    headers,
  });
  const text = await response.text();
  let data: Record<string, unknown> = {};
  if (text) {
    try {
      data = JSON.parse(text) as Record<string, unknown>;
    } catch {
      data = {"detail": response.ok ? "Invalid JSON response" : `HTTP ${response.status}`};
    }
  }
  if (!response.ok) {
    const detail = Array.isArray(data.detail)
      ? data.detail.map((item: { msg?: string }) => item.msg || "Ошибка заполнения").join("; ")
      : String(data.detail || `HTTP ${response.status}`);
    const error = new Error(detail) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }
  return data as T;
}

export const loadAuthMe = () => api<AuthMePayload>("/api/auth/me");
export const loadTelegramLoginOptions = () => (
  api<{ ok?: boolean; client_id: string; scope?: string[]; nonce: string }>("/api/auth/telegram/options")
);
export const completeTelegramLogin = (idToken: string) => (
  api<AuthMePayload>("/api/auth/telegram/login", {
    method: "POST",
    body: JSON.stringify({ id_token: idToken }),
  })
);
export const logoutAuth = () => api<Record<string, unknown>>("/api/auth/logout", { method: "POST" });
export const loadSettings = () => api<SettingsPayload>("/api/settings");
export const loadBillingSummary = () => api<BillingSummary>("/api/billing/summary");
export const loadBillingLedger = () => api<{tenant_code: string; items: BillingEntry[]}>("/api/billing/ledger?limit=200");
export const loadBillingPayments = () => api<{tenant_code: string; items: BillingPayment[]}>("/api/billing/payments?limit=50");
export const loadBillingSms = () => api<{tenant_code: string; items: BillingSmsUsage[]}>("/api/billing/sms?limit=200");
export const createBillingPayment = (amountRub: number) => api<{payment: BillingPayment; payment_action?: PaymentAction | null; message?: string}>("/api/billing/payments", {
  method: "POST",
  body: JSON.stringify({amount_rub: amountRub, idempotency_key: `miniapp:${Date.now()}:${Math.random().toString(36).slice(2)}`}),
});
export const loadOrders = (params: { q?: string; page?: number; limit?: number; filter?: string; mine?: boolean } = {}) => {
  const query = new URLSearchParams({
    q: params.q || "",
    page: String(params.page || 1),
    limit: String(params.limit || 12),
    filter: params.filter || "all",
    mine: params.mine ? "true" : "false",
    cdek_profile_code: "",
  });
  return api<OrdersPayload>(`/api/orders?${query.toString()}`);
};
export const loadOrderDetail = (orderValue: string) => (
  api<OrderDetailPayload>(`/api/orders/${encodeURIComponent(orderValue)}`)
);

export const createSupportHandoff = (orderReference = "") => (
  api<{ ok?: boolean; url?: string; expires_in_seconds?: number | null }>("/api/bot/support/handoff", {
    method: "POST",
    body: JSON.stringify({ order_reference: orderReference }),
  })
);

export const refreshOrderStatus = (orderValue: string) => (
  api<{ ok?: boolean; order?: OrderDetailPayload["order"] }>(
    `/api/orders/${encodeURIComponent(orderValue)}/refresh-status`,
    { method: "POST" },
  )
);

export const sendOrderDocument = (orderValue: string, documentType: "waybill" | "barcode") => (
  api<{ ok?: boolean; document_type?: string; result?: Record<string, unknown>; order?: OrderDetailPayload["order"] }>(
    `/api/orders/${encodeURIComponent(orderValue)}/send-document`,
    {
      method: "POST",
      body: JSON.stringify({ document_type: documentType }),
    },
  )
);

export const reprintOrderBarcode = (orderValue: string) => printIntent(`miniapp:${activeTenantCode}:${orderValue}`, key => (
  api<{ ok?: boolean; message?: string; attempt?: Record<string, unknown>; order?: OrderDetailPayload["order"] }>(
    `/api/orders/${encodeURIComponent(orderValue)}/print-barcode`,
    { method: "POST", headers: {"Idempotency-Key":key} },
  )
));

export const deleteOrder = (orderValue: string, reason: string) => (
  api<{ ok?: boolean; pending?: boolean; message?: string; order?: OrderDetailPayload["order"] }>(`/api/orders/${encodeURIComponent(orderValue)}/delete`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  })
);

export const findRecipientsByPhone = (phone: string) => (
  api<{ found?: boolean; recipient?: RecipientOption | null; recipients?: RecipientOption[] }>(
    `/api/recipients/by-phone?phone=${encodeURIComponent(phone)}`,
  )
);

export const findRecipientsByName = (name: string) => (
  api<{ found?: boolean; recipient?: RecipientOption | null; recipients?: RecipientOption[] }>(
    `/api/recipients/by-name?name=${encodeURIComponent(name)}`,
  )
);

export const searchCities = (query: string) => (
  api<CityOption[]>(`/api/cities?query=${encodeURIComponent(query)}`)
);

export const loadDeliveryPoints = (cityCode: number) => (
  api<DeliveryPointOption[]>(`/api/delivery-points?city_code=${encodeURIComponent(String(cityCode))}`)
);

export const findDeliveryPointByCode = (pvzCode: string) => (
  api<DeliveryPointLookupPayload>(
    `/api/delivery-points/by-code?pvz_code=${encodeURIComponent(pvzCode)}`,
  )
);

export const validateDeliveryPointPackages = (payload: {
  city_code: number;
  recipient_pvz: string;
  packages: PackageInput[];
  adult_goods?: boolean;
  manual_declared_value?: number | null;
}) => (
  api<{ valid?: boolean; point?: DeliveryPointOption }>("/api/delivery-points/validate-packages", {
    method: "POST",
    body: JSON.stringify(payload),
  })
);

export const validateMoyskladOrders = (moyskladOrderNumber: string) => (
  api<{ ok?: boolean; numbers?: string[] }>("/api/moysklad-orders/validate", {
    method: "POST",
    body: JSON.stringify({ moysklad_order_number: moyskladOrderNumber }),
  })
);

export const calculateTariffs = (payload: {
  city_code: number;
  packages: PackageInput[];
  adult_goods?: boolean;
  manual_declared_value?: number | null;
}) => (
  api<TariffOption[]>("/api/tariffs", {
    method: "POST",
    body: JSON.stringify(payload),
  })
);

export const loadTariffDetails = (payload: {
  city_code: number;
  tariff_code: number;
  packages: PackageInput[];
  adult_goods?: boolean;
  manual_declared_value?: number | null;
}) => (
  api<TariffDetail>("/api/tariff-details", {
    method: "POST",
    body: JSON.stringify(payload),
  })
);

export const preflightOrder = (payload: OrderCreatePayload) => (
  api<OrderPreflightPayload>("/api/orders/preflight", {
    method: "POST",
    body: JSON.stringify(payload),
  })
);

export const createOrder = (payload: OrderCreatePayload, idempotencyKey: string) => (
  api<OrderCreateResult>("/api/orders", {
    method: "POST",
    headers: {"X-Idempotency-Key": idempotencyKey},
    body: JSON.stringify(payload),
  })
);
